import { UserRoles } from '@account-users/account/models';
import { AuthService } from '@account-users/auth/auth.service';
import { RoleType } from '@account-users/user/models/UserRole';
import { UserService } from '@account-users/user/services/user.service';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserRolesAndPermissionsService {
  public hasOnlyTrialDispenserRole$: Observable<boolean>;
  public hasTrialDispenserRole$: Observable<boolean>;
  public hasOrderManagerRole$: Observable<boolean>;
  public hasWawRole$: Observable<boolean>;
  public hasUserRoles$: Observable<boolean>;

  private _userRoles$: Observable<UserRoles[]>;

  public get userRoles() {
    return this._userRoles$;
  }

  constructor(
    private readonly authService: AuthService,
    private readonly userService: UserService,
  ) {
    if (!this._userRoles$) {
      this._userRoles$ = this.authService.isAuthenticated$.pipe(
        filter(Boolean),
        switchMap(() =>
          this.init(this.authService?.profile?.userId).pipe(
            map(({ userRoles, roles }) => {
              return userRoles?.filter((role) => {
                const ur = roles.find((r) => r.code === role);
                return ur && ur.type === RoleType.User;
              });
            }),
          ),
        ),
        take(1),
        shareReplay(1),
      );
    }

    this.hasOnlyTrialDispenserRole$ = this._userRoles$.pipe(
      map(
        (roles) => roles?.length === 1 && roles[0] === UserRoles.TrialDispenser,
      ),
    );
    this.hasTrialDispenserRole$ = this._userRoles$.pipe(
      map((roles) => roles.includes(UserRoles.TrialDispenser)),
    );
    this.hasOrderManagerRole$ = this._userRoles$.pipe(
      map((roles) => roles.includes(UserRoles.OrderManager)),
    );
    this.hasWawRole$ = this._userRoles$.pipe(
      map((roles) => roles.includes(UserRoles.Waw)),
    );
    this.hasUserRoles$ = this._userRoles$.pipe(map((roles) => !!roles?.length));
  }

  private init(userId: string) {
    return forkJoin([
      this.userService.fetchUser(userId),
      this.userService.fetchUserRoles(),
    ]).pipe(
      map(([user, roles]) => ({ userRoles: user['user']?.roles, roles })),
    );
  }
}
